import {
  engineAllowsModel,
  HISTORICAL_MODEL_LABELS,
  isModelAllowedForHarness,
  labelForModelId,
  PROVIDERS_BY_HARNESS,
  resolveAgentModel,
  resolveCapabilities,
  type AgentMode,
  type HarnessId,
  type ModelProvider,
} from "@bash/shared";

/** Re-exported for the catalog-in-registry tripwire below, which reads the delisted ids by name. */
export { HISTORICAL_MODEL_LABELS };

/**
 * The composer's model picker.
 *
 * There is ONE engine — the native harness — so there is no harness to pick.
 * The two-pane version (Claude / OpenAI / Vestra on the left, models scoped to
 * the choice on the right) died with the arms; the left pane went at the
 * 2026-07-10 redesign and its data model (HARNESS_OPTIONS) went when the arms
 * were deleted. Native runs every provider by selecting the transport from the
 * MODEL's wire dialect, which is exactly why picking a model is now sufficient.
 *
 * Curated display labels live here so the rows stay clean (no raw ids, no
 * "Claude" in prose). The model list is DERIVED: filter the model set to the
 * providers native allows, so it can never drift from the shared registry.
 * Provider-name group headers are an accepted carve-out (founder, 2026-06-23).
 */

/**
 * The DISPLAY brand a model groups under in the picker — what the USER sees (the
 * model family), DECOUPLED from the infra `provider` (the credential/transport lane).
 * For most models brand === provider. The Fireworks open-model lane splits by family
 * (DeepSeek + GLM) because users pick a model brand, not the host: GLM rides the
 * `fireworks` infra provider but keeps its own 'zhipu' (Z.ai) display brand, so
 * 'zhipu' lives here as a DISPLAY-ONLY brand even though it is no longer a ModelProvider.
 */
export type Brand = ModelProvider | "deepseek" | "zhipu";

/** A single model row (right pane). */
export interface ModelRow {
  id: string;
  label: string;
  blurb: string;
  /** Infra provider (credential lane + harness gating). */
  provider: ModelProvider;
  /** Display brand (picker group + icon). */
  brand: Brand;
}

/** Map a model to its display brand. Fireworks → per-family (GLM keeps its own 'zhipu'
 *  Z.ai brand, DeepSeek its own); everything else → provider. */
function brandForModel(id: string, provider: ModelProvider): Brand {
  if (provider === "fireworks" && id.startsWith("glm")) return "zhipu";
  if (provider === "fireworks" && id.startsWith("deepseek")) return "deepseek";
  return provider;
}

/**
 * The full curated model set. Each row's provider is resolved from the shared
 * registry (resolveCapabilities) so scoping is registry-driven, not hardcoded.
 * Curated labels/blurbs are the product copy.
 */
const ALL_MODELS: readonly ModelRow[] = [
  // Fable 5: delisted 2026-07-16 (cost), founder-restored 2026-07-22.
  // Fable 5.1 (released 2026-09-01) replaces Fable 5 in the picker; Fable 5 is
  // legacy — still resolvable + billable for historical tasks, just no longer
  // offered. Same treatment as Opus 4.8 below.
  {
    id: "claude-fable-5-1",
    label: "Fable 5.1",
    blurb: "The flagship. Frontier intelligence for the hardest work.",
    provider: resolveCapabilities("claude-fable-5-1").provider,
  },
  // Opus 5.5 (2026-09-22) replaces Opus 5 in the picker, as Opus 5 replaced 4.8
  // (2026-07-24); both are legacy — still resolvable + billable for historical
  // tasks, just no longer offered.
  {
    id: "claude-opus-5-5",
    label: "Opus 5.5",
    blurb: "Deepest reasoning for the hard things.",
    provider: resolveCapabilities("claude-opus-5-5").provider,
  },
  // Sonnet 5.5 (2026-09-28) replaces Sonnet 5 in the picker (2026-09-29); Sonnet 5
  // is legacy — still resolvable + billable for historical tasks, just no longer
  // offered, and a stored Sonnet 5 pick follows to 5.5 (migratePick).
  {
    id: "claude-sonnet-5-5",
    label: "Sonnet 5.5",
    blurb: "The best mix of speed and intelligence.",
    provider: resolveCapabilities("claude-sonnet-5-5").provider,
  },
  // Haiku 5.5 (2026-10-07) replaces Haiku 4.5, which is removed: every id naming
  // it runs 5.5 (the registry's successor), a stored pick included.
  {
    id: "claude-haiku-5-5",
    label: "Haiku 5.5",
    blurb: "Fast and economical.",
    provider: resolveCapabilities("claude-haiku-5-5").provider,
  },
  // GPT-6 Astra (2026-09-05, founder's call) leads the OpenAI house above Sol.
  // Sol stays the DB-managed default; Astra is a pick, never a fallback.
  {
    id: "gpt-6-astra",
    label: "GPT-6 Astra",
    blurb: "The new flagship. Deepest reasoning.",
    provider: resolveCapabilities("gpt-6-astra").provider,
  },
  // GPT-6 Sol / Luna (2026-09-23) replace GPT-5.6 Sol / Luna; 5.6 Terra is
  // retired for good (GPT-6 Sol is its successor). All three 5.6 ids are legacy:
  // still resolvable + billable for historical tasks, never offered again.
  // GPT-6.1 Sol takes GPT-6 Sol's slot (2026-09-30); GPT-6 Sol is legacy the same
  // way, and a stored pick on it follows to 6.1 (migratePick).
  {
    id: "gpt-6.1-sol",
    label: "GPT-6.1 Sol",
    blurb: "Frontier flagship for the hardest problems.",
    provider: resolveCapabilities("gpt-6.1-sol").provider,
  },
  {
    id: "gpt-6-luna",
    label: "GPT-6 Luna",
    blurb: "Fast and economical.",
    provider: resolveCapabilities("gpt-6-luna").provider,
  },
  // Grok 4.7 replaces Grok 4.5 and 4.6 in the picker (2026-09-29, founder's
  // delist); both are legacy — still resolvable + billable for historical tasks,
  // and a stored pick on either follows to 4.7 (migratePick).
  {
    id: "grok-4.7",
    label: "Grok 4.7",
    blurb: "The newest Grok, with real-time instincts.",
    provider: resolveCapabilities("grok-4.7").provider,
  },
  // Gemini 4 Argon (Google) — the flagship, on the same wire, 2026-10-17.
  // It leads its house, as Fable and Astra lead theirs.
  {
    id: "gemini-4-argon",
    label: "Gemini 4 Argon",
    blurb: "Google's flagship, for deep reasoning.",
    provider: resolveCapabilities("gemini-4-argon").provider,
  },
  // Gemini 3.8 Flash (Google) — the gemini_interactions dialect, 2026-09-03.
  {
    id: "gemini-3.8-flash",
    label: "Flash 3.8",
    blurb: "Fast frontier Flash for long-running work.",
    provider: resolveCapabilities("gemini-3.8-flash").provider,
  },
].map((m): ModelRow => ({ ...m, brand: brandForModel(m.id, m.provider) }));

/** Brand → group header label (founder carve-out: brand names allowed). DeepSeek is
 *  its own brand even though it rides the Fireworks lane — users pick the model, not the host. */
const BRAND_LABEL: Record<Brand, string> = {
  anthropic: "Anthropic",
  openai: "OpenAI",
  zhipu: "Z.ai",
  fireworks: "Fireworks",
  deepseek: "DeepSeek",
  // xAI rebranded SpaceXAI 2026-07-06 (full merger into SpaceX).
  xai: "SpaceXAI",
  google: "Google",
};

/** Stable display order of brand groups within a harness panel. */
/** Founder order (08-13): OpenAI, SpaceXAI, DeepSeek, Anthropic, Z.ai. */
const BRAND_ORDER: readonly Brand[] = [
  "openai",
  "xai",
  "deepseek",
  "anthropic",
  "zhipu",
  "fireworks",
  "google",
];

/**
 * A picker row plus its DISPLAY-ONLY availability state. `comingSoon` carries the
 * registry's user-facing reason when the model is registry-gated (`available:false`,
 * e.g. GPT-5.6 while our account clears OpenAI's preview) — the model menu shows
 * the row disabled so users see the upcoming lineup without being able to launch a
 * task that would fail. Pickability/validation stays pickableModels-only: gated
 * ids are still rejected by isModelPickable and the server submit-gate.
 */
export interface DisplayModelRow extends ModelRow {
  comingSoon?: string;
}

export interface ProviderGroup {
  /** Display brand (group identity + icon) — NOT the infra provider. */
  brand: Brand;
  header: string;
  rows: DisplayModelRow[];
}

/**
 * The model rows a user can pick. Native is the only engine and it runs every
 * provider, so this is the whole registry-allowed catalog. It used to be scoped
 * per harness (Claude → Anthropic only, OpenAI → OpenAI only); that scoping died
 * with the arms.
 *
 * A model the registry marks UNAVAILABLE (`available === false`) is excluded so it
 * can never be picked. The shared label table keeps every delisted row, so a
 * historical task on any model still labels correctly.
 */
export function pickableModels(): ModelRow[] {
  return ALL_MODELS.filter(
    (m) =>
      PROVIDERS_BY_HARNESS.native.includes(m.provider) &&
      resolveCapabilities(m.id).available !== false
  );
}

/** The DISPLAY rows for `harness`, grouped by display BRAND in display order.
 *  Unlike pickableModels (pickability), this keeps registry-gated rows and marks
 *  them `comingSoon` so the panel can render them disabled with the reason.
 *  Vestra (native) runs every provider, so its set IS the catalog; Claude Code,
 *  the person's own plan (engines T2b), runs the Anthropic house alone; Codex
 *  (engines T3) runs only the ids its CLI lists (ENGINE_MODEL_IDS), never every
 *  GPT row Vestra has. */
export function modelGroups(harness: HarnessId = "native"): ProviderGroup[] {
  const allowed = PROVIDERS_BY_HARNESS[harness];
  const rows: DisplayModelRow[] = ALL_MODELS.filter(
    (m) => allowed.includes(m.provider) && engineAllowsModel(harness, m.id)
  ).map((m) => {
    const caps = resolveCapabilities(m.id);
    return caps.available === false
      ? { ...m, comingSoon: caps.unavailableReason ?? "Available soon." }
      : m;
  });
  const groups: ProviderGroup[] = [];
  for (const brand of BRAND_ORDER) {
    const groupRows = rows.filter((r) => r.brand === brand);
    if (groupRows.length > 0) {
      groups.push({ brand, header: BRAND_LABEL[brand], rows: groupRows });
    }
  }
  return groups;
}

/**
 * The model a NEW task on `harness` runs when the pill holds `modelId`: that
 * model when the engine can run it, else the engine's own default for the mode
 * (engines T2b: a Vestra pick like Luna on Claude Code shows, and runs, the
 * plan's Sonnet). The server resolves an absent pin the same way.
 */
export function modelForEngine(
  modelId: string,
  harness: HarnessId,
  mode?: AgentMode
): string {
  return engineRunsModel(modelId, harness)
    ? modelId
    : resolveAgentModel(mode, harness);
}

/** True when `harness` can run `modelId`: its provider, and the engine's own
 *  list where it has one (engines T3: Codex). */
export function engineRunsModel(modelId: string, harness: HarnessId): boolean {
  const caps = resolveCapabilities(modelId);
  return (
    isModelAllowedForHarness(caps.provider, harness) &&
    engineAllowsModel(harness, caps.id)
  );
}

/** True when `modelId` may be picked. */
export function isModelPickable(modelId: string | undefined): boolean {
  if (!modelId) return false;
  return pickableModels().some((r) => r.id === modelId);
}

/**
 * The ids currently pickable (the full catalog). Stores import this to validate a
 * persisted model choice.
 */
export const PICKABLE_MODEL_IDS: ReadonlySet<string> = new Set(
  // Registry-gated (dark) rows are display-only, never pickable — excluding them
  // keeps the modelStore self-heal honest: a persisted dark id resets to the
  // default instead of flowing to a server that would reject it.
  ALL_MODELS.filter((m) => resolveCapabilities(m.id).available !== false).map(
    (m) => m.id
  )
);

/** Curated family label for a model id (falls back to Sonnet). The table is
 *  shared with the server (R-1b-8): the settings cards speak these same names,
 *  so a rename here reaches both surfaces at once. */
export function labelForModel(id: string | undefined): string {
  return labelForModelId(id) || "Sonnet 5.5";
}

/**
 * SHORT names for the composer pill — the pill shows [provider icon][short name],
 * so the brand prefix is redundant next to its own mark (DeepSeek whale + "Flash",
 * not "DeepSeek V4 Flash"). Only ids whose label repeats the brand get an entry;
 * everything else falls back to the curated family label.
 */
const SHORT_LABELS: Record<string, string> = {
  "gpt-6-astra": "Astra",
  "gpt-6.1-sol": "Sol",
  "gpt-6-luna": "Luna",
  // GPT-6 Sol left the picker 2026-09-30; an old task still reads the short name.
  "gpt-6-sol": "Sol",
  // The 5.6 ids left the picker 2026-09-23; an old task still reads the short
  // name, since the crown and the Line never say the brand (LineWork).
  "gpt-5.6-sol": "Sol",
  "gpt-5.6-terra": "Terra",
  "gpt-5.6-luna": "Luna",
  "deepseek-v4-pro": "Pro",
  "deepseek-v4-flash": "Flash",
  "gemini-3.8-flash": "Flash",
  "gemini-4-argon": "Argon",
};

/** Pill-length name for a model id: curated short form, else the family label. */
export function shortLabelForModel(id: string | undefined): string {
  return (id && SHORT_LABELS[id]) || labelForModel(id);
}

/**
 * Display BRAND for a model id — drives the pill's provider icon. Catalog rows
 * carry their brand; non-catalog ids (historical tasks, e.g. sonnet-4-6) derive
 * it from the registry provider through the same brandForModel split.
 */
export function brandForModelId(id: string | undefined): Brand {
  if (!id) return "anthropic";
  const row = ALL_MODELS.find((m) => m.id === id);
  return row?.brand ?? brandForModel(id, resolveCapabilities(id).provider);
}
